import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';
import LanguageIcon from '@mui/icons-material/Language';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    List,
    ListItem,
    Stack,
    Tab,
    Tabs,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import EditTrackingService from '../../services/EditTrackingService';

export default function UserEditStats({ folderPath, videoId, filterMode, dateRange = 'last30days' }) {
    const [stats, setStats] = useState(null);
    const [videoStats, setVideoStats] = useState([]);
    const [languageStats, setLanguageStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [videoLanguageLoading, setVideoLanguageLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        const fetchUserStats = async () => {
            setLoading(true);
            try {
                // Get basic stats
                const data = await EditTrackingService.getUserStats(folderPath, videoId, dateRange);
                console.log('Fetched user stats:', data);
                if (!data) {
                    setStats(null);
                    return;
                }
                const derivedTotalTime = data?.totalTimeMs ?? Object.values(data?.totalTimeByLevel || {}).reduce((acc, time) => acc + time, 0);
                setStats({
                    ...data,
                    totalTime: derivedTotalTime,
                });

                
                // Also load detailed stats
                fetchDetailedStats();
            } catch (error) {
                console.error('Error fetching user stats:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchUserStats();
    }, [folderPath, videoId, filterMode, dateRange]);
    
    const fetchDetailedStats = async () => {
        setVideoLanguageLoading(true);
        try {
            // Get video breakdown
            const videos = await EditTrackingService.getUserVideoBreakdown(folderPath, videoId, dateRange);
            setVideoStats(videos);
            
            // Get language breakdown
            const languages = await EditTrackingService.getUserLanguageBreakdown(folderPath, videoId, dateRange);
            setLanguageStats(languages);
        } catch (error) {
            console.error('Error fetching detailed stats:', error);
        } finally {
            setVideoLanguageLoading(false);
        }
    };

    const formatTime = (ms) => {
        if (!ms) return '00:00:00';
        return EditTrackingService.formatTime(ms);
    };
    
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const levelTimeEntries = Object.entries(stats?.totalTimeByLevel || {}).sort((a, b) => b[1] - a[1]);
    const levelEditEntries = Object.entries(stats?.totalEditsByLevel || {}).sort((a, b) => b[1] - a[1]);

    const summaryCards = [
        { key: 'sessions', label: 'Total Sessions', value: stats?.totalSessions || 0 },
        { key: 'time', label: 'Total Time', value: formatTime(stats?.totalTime || 0) },
        { key: 'hours', label: 'Hours', value: (stats?.totalHours || 0).toFixed(2) },
        { key: 'videos', label: 'Videos Edited', value: stats?.totalVideosEdited || 0 },
        { key: 'languages', label: 'Languages Worked', value: stats?.totalLanguagesWorked || 0 },
        { key: 'edits', label: 'Total Edits', value: stats?.totalEdits || 0 },
    ];

    const dateRangeLabel = {
        last24hours: 'Last 24 Hours',
        last7days: 'Last 7 Days',
        last30days: 'Last 30 Days',
        last90days: 'Last 90 Days',
        currentMonth: 'Current Month',
        previousMonth: 'Previous Month',
        all: 'All Available Data',
    }[dateRange] || 'Last 30 Days';

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    if (!stats) {
        return (
            <Typography variant="body1" color="text.secondary" p={2}>
                No editing statistics available.
            </Typography>
        );
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    My Editing Stats
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Review your overall activity first, then drill into videos and languages.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={`Range: ${dateRangeLabel}`} variant="outlined" />
                    {folderPath && folderPath !== '/' && (
                        <Chip size="small" label={`Folder: ${folderPath}`} variant="outlined" />
                    )}
                    {videoId && (
                        <Chip size="small" label={`Video: ${videoId}`} variant="outlined" />
                    )}
                </Stack>
            </Box>
            
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
                <Tab 
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AccessTimeIcon sx={{ mr: 1 }} />
                            Summary
                        </Box>
                    } 
                />
                <Tab 
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <VideocamIcon sx={{ mr: 1 }} />
                            Videos
                        </Box>
                    } 
                />
                <Tab 
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LanguageIcon sx={{ mr: 1 }} />
                            Languages
                        </Box>
                    } 
                />
            </Tabs>
            
            {/* Summary Tab */}
            {activeTab === 0 && (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Overall Summary
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Grid container spacing={2}>
                                    {summaryCards.map((card) => (
                                        <Grid item xs={12} sm={6} md={4} lg={2} key={card.key}>
                                            <Box sx={{ textAlign: 'center', p: 1.25, borderRadius: 1.5, bgcolor: 'action.hover' }}>
                                                <Typography variant="h5" sx={{ fontWeight: 600 }}>{card.value}</Typography>
                                                <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Time by Level
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                
                                {levelTimeEntries.map(([level, time]) => (
                                    <Box key={level} sx={{ mb: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary">{level}</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatTime(time)}</Typography>
                                        </Box>
                                    </Box>
                                ))}
                                
                                {levelTimeEntries.length === 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        No time data available
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Edits by Level
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                
                                {levelEditEntries.map(([level, edits]) => (
                                    <Box key={level} sx={{ mb: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary">{level}</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>{edits || 0}</Typography>
                                        </Box>
                                    </Box>
                                ))}
                                
                                {levelEditEntries.length === 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        No edit data available
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}
            
            {/* Videos Tab */}
            {activeTab === 1 && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Videos You've Worked On
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        {videoLanguageLoading ? (
                            <Box display="flex" justifyContent="center" p={3}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : videoStats && videoStats.length > 0 ? (
                            <List>
                                <ListItem sx={{ bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <VideocamIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                <Typography variant="subtitle2" color="text.secondary">Video</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={4} sm={2}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                <Typography variant="subtitle2" color="text.secondary">Time</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={4} sm={2}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <EditIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                <Typography variant="subtitle2" color="text.secondary">Edits</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={4} sm={2}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <CalendarTodayIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                <Typography variant="subtitle2" color="text.secondary">Sessions</Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </ListItem>
                                {videoStats.map((video) => (
                                    <ListItem key={video.videoId} divider>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle1">{video.videoName || video.videoId}</Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                    {(video.languages || []).map(lang => (
                                                        <Chip 
                                                            key={lang} 
                                                            label={lang} 
                                                            size="small" 
                                                            variant="outlined"
                                                            sx={{ mr: 0.5 }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4} sm={2}>
                                                <Typography>{formatTime(video.time)}</Typography>
                                            </Grid>
                                            <Grid item xs={4} sm={2}>
                                                <Typography>{video.edits || 0}</Typography>
                                            </Grid>
                                            <Grid item xs={4} sm={2}>
                                                <Typography>{video.sessionsCount || 0} sessions</Typography>
                                            </Grid>
                                        </Grid>
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No video data available
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            )}
            
            {/* Languages Tab */}
            {activeTab === 2 && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Languages You've Worked On
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        {videoLanguageLoading ? (
                            <Box display="flex" justifyContent="center" p={3}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : languageStats && languageStats.length > 0 ? (
                            <List>
                                <ListItem sx={{ bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <LanguageIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                <Typography variant="subtitle2" color="text.secondary">Language</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} sm={2}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <VideocamIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                <Typography variant="subtitle2" color="text.secondary">Videos</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} sm={2}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                <Typography variant="subtitle2" color="text.secondary">Time</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} sm={2}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <EditIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                <Typography variant="subtitle2" color="text.secondary">Edits</Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </ListItem>
                                {languageStats.map((lang) => (
                                    <ListItem key={lang.language} divider>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle1">{lang.language}</Typography>
                                            </Grid>
                                            <Grid item xs={6} sm={2}>
                                                <Typography>{lang.videoCount || 0}</Typography>
                                            </Grid>
                                            <Grid item xs={6} sm={2}>
                                                <Typography>{formatTime(lang.time)}</Typography>
                                            </Grid>
                                            <Grid item xs={6} sm={2}>
                                                <Typography>{lang.edits || 0}</Typography>
                                            </Grid>
                                        </Grid>
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No language data available
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            )}
        </Box>
    );
}